import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { PartnerUser, Partner } from '@/types';

interface AuthContextType {
  user: FirebaseUser | null;
  userData: PartnerUser | null;
  partner: Partner | null;
  loading: boolean;
  isPartner: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, name: string) => Promise<FirebaseUser>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<PartnerUser | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (firebaseUser: FirebaseUser) => {
    try {
      // Fetch user document
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const partnerUserData: PartnerUser = {
          id: userDoc.id,
          email: data.email || firebaseUser.email || '',
          name: data.name || '',
          role: data.role || 'member',
          isPartner: data.isPartner || false,
          partnerId: data.partnerId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        };
        setUserData(partnerUserData);

        // If user is a partner, fetch partner data
        if (partnerUserData.isPartner && partnerUserData.partnerId) {
          const partnerDoc = await getDoc(doc(db, 'partners', partnerUserData.partnerId));
          if (partnerDoc.exists()) {
            const partnerData = partnerDoc.data();
            setPartner({
              id: partnerDoc.id,
              ...partnerData,
              createdAt: partnerData.createdAt?.toDate(),
              updatedAt: partnerData.updatedAt?.toDate(),
              approvedAt: partnerData.approvedAt?.toDate(),
              suspendedAt: partnerData.suspendedAt?.toDate(),
            } as Partner);
          }
        }
      } else {
        setUserData(null);
        setPartner(null);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserData(null);
      setPartner(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      
      if (firebaseUser) {
        await fetchUserData(firebaseUser);
      } else {
        setUserData(null);
        setPartner(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string, name: string) => {
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user document
    await setDoc(doc(db, 'users', newUser.uid), {
      email,
      name,
      role: 'member',
      isPartner: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return newUser;
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setUserData(null);
    setPartner(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshUserData = async () => {
    if (user) {
      await fetchUserData(user);
    }
  };

  const isPartner = userData?.isPartner || false;
  const isSuperAdmin = userData?.role === 'super_admin';

  const value = {
    user,
    userData,
    partner,
    loading,
    isPartner,
    isSuperAdmin,
    signIn,
    signUp,
    signOut,
    resetPassword,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

